import type { PaginationState } from "@tanstack/react-table";
import type { FilterValues, SortConfig } from "../../datagrid-configs/types";

export const TABLE_URL_PAGE_PARAM = "page";
export const TABLE_URL_PAGE_SIZE_PARAM = "limit";
export const TABLE_URL_SORT_PARAM = "sort";
export const TABLE_URL_SORT_DIR_PARAM = "dir";
export const TABLE_URL_FILTERS_PARAM = "filters";

export interface TableStateFromUrl {
	pagination: Pick<PaginationState, "pageIndex">;
	pageSizeFromUrl: number | null;
	sort: SortConfig | undefined;
	filterValues: FilterValues;
}

function parseFiltersParam(raw: string | null): FilterValues {
	if (raw === null || raw === "") {
		return {};
	}

	try {
		const parsed: unknown = JSON.parse(raw);
		if (parsed !== null && typeof parsed === "object" && Array.isArray(parsed) === false) {
			return parsed as FilterValues;
		}
	} catch {
		return {};
	}

	return {};
}

function hasFilterValues(filters: FilterValues): boolean {
	return Object.keys(filters).some((key) => {
		const value = filters[key];
		if (value === null || value === undefined || value === "") {
			return false;
		}
		if (Array.isArray(value)) {
			return value.length > 0;
		}
		if (typeof value === "object") {
			return Object.values(value as Record<string, unknown>).some(
				(nested) => nested !== null && nested !== undefined && nested !== "",
			);
		}
		return true;
	});
}

/** Read table pagination, sort, and filters from the current URL search params. */
export function readTableStateFromUrl(searchParams: URLSearchParams): TableStateFromUrl {
	const pageParam = searchParams.get(TABLE_URL_PAGE_PARAM);
	const parsedPage = pageParam !== null ? Number(pageParam) : Number.NaN;
	const pageIndex = Number.isFinite(parsedPage) && parsedPage > 1 ? parsedPage - 1 : 0;

	const pageSizeParam = searchParams.get(TABLE_URL_PAGE_SIZE_PARAM);
	const parsedPageSize = pageSizeParam !== null ? Number(pageSizeParam) : Number.NaN;
	const pageSizeFromUrl = Number.isFinite(parsedPageSize) && parsedPageSize > 0 ? parsedPageSize : null;

	const sortField = searchParams.get(TABLE_URL_SORT_PARAM);
	const sortDirection = searchParams.get(TABLE_URL_SORT_DIR_PARAM);
	const sort =
		sortField !== null && sortField !== "" && sortDirection !== null && sortDirection !== ""
			? { field: sortField, direction: sortDirection }
			: undefined;

	return {
		pagination: { pageIndex },
		pageSizeFromUrl,
		sort,
		filterValues: parseFiltersParam(searchParams.get(TABLE_URL_FILTERS_PARAM)),
	};
}

/** Build URL search params from current table state (omits defaults to keep URLs short). */
export function buildTableUrlParams(args: {
	pagination: PaginationState;
	sort: SortConfig | undefined;
	filterValues: FilterValues;
}): Record<string, string | null> {
	const { pagination, sort, filterValues } = args;
	const params: Record<string, string | null> = {};

	if (pagination.pageIndex > 0) {
		params[TABLE_URL_PAGE_PARAM] = String(pagination.pageIndex + 1);
	} else {
		params[TABLE_URL_PAGE_PARAM] = null;
	}

	params[TABLE_URL_PAGE_SIZE_PARAM] = String(pagination.pageSize);

	if (sort !== undefined) {
		params[TABLE_URL_SORT_PARAM] = sort.field;
		params[TABLE_URL_SORT_DIR_PARAM] = sort.direction;
	} else {
		params[TABLE_URL_SORT_PARAM] = null;
		params[TABLE_URL_SORT_DIR_PARAM] = null;
	}

	if (hasFilterValues(filterValues)) {
		params[TABLE_URL_FILTERS_PARAM] = JSON.stringify(filterValues);
	} else {
		params[TABLE_URL_FILTERS_PARAM] = null;
	}

	return params;
}

/** Returns the last visited list URL (with query params) for detail-page back links. */
export function getTableListBackUrl(listPath: string): string {
	const stored = sessionStorage.getItem(`tableListUrl:${listPath}`);
	return stored ?? listPath;
}
